import { URLS } from "@core/shared/utils/urls";
import { useIsLoggedIn } from "@framework/account/hooks";
import { userAtom } from "@framework/config/atoms";
import { platformFeaturesAtom } from "@framework/shared/atoms";
import { useFetcher, useLocation } from "@remix-run/react";
import { useEffect } from "react";

type InquiryUnreadData = {
  count?: number;
};

const refreshIntervalMs = 5 * 60 * 1000;

/**
 * Unread admin-reply count for the logged-in student, refreshed on every
 * navigation. Returns 0 (and never requests) when the tenant has not released
 * inquiries or the viewer is not a student.
 *
 * @example
 * const { isEnabled, unreadCount } = useInquiryUnreadCount();
 */
export function useInquiryUnreadCount() {
  const isLoggedIn = useIsLoggedIn();
  const user = userAtom.useValue();
  const isReleased = platformFeaturesAtom.useValue().inquiries === true;
  const fetcher = useFetcher<InquiryUnreadData>();
  const location = useLocation();
  const isEnabled = isLoggedIn && isReleased && user?.isStudent === true;

  useEffect(() => {
    if (!isEnabled) return;

    fetcher.load(URLS.inquiries.unread);
    // fetcher identity is stable enough; reloading is keyed on navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, location.pathname]);

  useEffect(() => {
    if (!isEnabled) return;

    const refresh = () => fetcher.load(URLS.inquiries.unread);

    window.addEventListener("inquiries:read", refresh);
    const timer = window.setInterval(refresh, refreshIntervalMs);

    return () => {
      window.removeEventListener("inquiries:read", refresh);
      window.clearInterval(timer);
    };
    // Refreshes only on the custom read event; fetcher identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);

  return {
    isEnabled,
    unreadCount: isEnabled ? Number(fetcher.data?.count) || 0 : 0,
  };
}
